import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { tutorsAPI, resolveImage } from '../utils/api';
import { useTranslation } from 'react-i18next';

// Helper to safely extract text based on current language
const tField = (field, lang = 'en') => {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (Array.isArray(field)) return field.join(' • '); // For string arrays
  
  const targetLang = lang.startsWith('zh') ? 'zh' : 'en';
  const fallbackLang = targetLang === 'en' ? 'zh' : 'en';

  if (field[targetLang]) {
    if (Array.isArray(field[targetLang])) return field[targetLang].join(' • ');
    return field[targetLang];
  }
  if (field[fallbackLang]) {
    if (Array.isArray(field[fallbackLang])) return field[fallbackLang].join(' • ');
    return field[fallbackLang];
  }
  return '';
};

const LEVEL_BADGE = { Beginner:'badge-blue', Intermediate:'badge-orange', Advanced:'badge-gold' };

export default function TutorDetail() {
  const { t, i18n } = useTranslation();
  const { id }  = useParams();
  const navigate = useNavigate();
  const [tutor, setTutor]     = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoad]    = useState(true);

  const load = useCallback(() => {
    tutorsAPI.getById(id)
      .then(r => { 
        setTutor(r.data); 
        setBatches(r.data.batches || []); 
      })
      .catch(console.error)
      .finally(() => setLoad(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Layout title="Faculty"><div className="dash-loading">⏳ Loading...</div></Layout>;
  if (!tutor)  return <Layout title="Faculty"><div className="dash-card"><div className="dash-empty"><div className="dash-empty-title">Tutor not found</div></div></div></Layout>;

  return (
    <Layout title={tField(tutor.name, i18n.language)}>
      <button className="btn btn-outline btn-sm mb-6" onClick={() => navigate('/faculty')}>{t('common.backToFaculty', '← BACK')}</button>

      {/* Profile Banner */}
      <div className="dash-profile-banner mb-6">
        <div className="dash-profile-badge">{tutor.rating || 4.5} ⭐</div>
        <div className="dash-avatar-lg" style={{ backgroundImage: tutor.photoUrl ? `url("${resolveImage(tutor.photoUrl)}")` : 'none', backgroundSize:'cover' }}>
          {!tutor.photoUrl && (tField(tutor.name, i18n.language) || '?').split(' ').map(w => w[0]).slice(0,2).join('').toUpperCase()}
        </div>
        <div className="dash-profile-name">{tField(tutor.name, i18n.language)}</div>
        <div className="dash-profile-specialty">{tField(tutor.specialization, i18n.language)}</div>
        <div style={{ display:'flex', gap:20, justifyContent:'center', marginBottom:20, fontSize:13, color:'rgba(255,255,255,.5)' }}>
          <span>✉️ {tutor.email}</span>
          {tutor.mobile && <span>📞 {tutor.mobile}</span>}
        </div>
        <div className="dash-profile-stats">
          <div className="dash-profile-stat"><div className="num">{batches.length}</div><div className="lbl">Batches</div></div>
          <div className="dash-profile-stat"><div className="num">{batches.reduce((s,b) => s+(b.students?.length||0), 0)}</div><div className="lbl">Total Students</div></div>
          <div className="dash-profile-stat"><div className="num" style={{ color: tutor.status === 'Active' ? '#4caf86' : '#e05252' }}>●</div><div className="lbl">{tutor.status}</div></div>
        </div>
      </div>

      <div className="grid-2 mb-6">
        <div className="dash-card h-full">
          <h3 className="dash-card-title mb-4">Professional Background</h3>
          <div className="text-sm text-muted mb-2" style={{ textTransform:'uppercase', fontSize:10, fontWeight:700, letterSpacing:1 }}>{t('tutorDetail.experience')}</div>
          <p className="mb-4">{tutor.experience || t('tutorDetail.noExp')}</p>
          <div className="text-sm text-muted mb-2" style={{ textTransform:'uppercase', fontSize:10, fontWeight:700, letterSpacing:1 }}>{t('tutorDetail.bio')}</div>
          <p className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>{tField(tutor.bio, i18n.language) || t('tutorDetail.noBio')}</p>
        </div>
        <div className="dash-card h-full">
          <h3 className="dash-card-title mb-4">{t('tutorDetail.accountMeta')}</h3>
          <div className="text-sm mb-4">
            <div className="flex-between mb-2"><span>{t('tutorDetail.joinDate')}</span> <span>{new Date(tutor.joiningDate).toLocaleDateString()}</span></div>
            <div className="flex-between mb-2"><span>{t('tutorDetail.salary')}</span> <span>${tutor.salary}</span></div>
            <div className="flex-between"><span>{t('tutorDetail.emergency')}</span> <span>{tutor.emergencyContact || t('tutorDetail.notSet')}</span></div>
          </div>
        </div>
      </div>

      {/* Batches */}
      <div className="dash-list-header mb-4">
        <h2 className="dash-card-title">{t('tutorDetail.assignedBatches')} ({batches.length})</h2>
      </div>

      <div className="dash-card">
        {batches.length === 0 ? (
          <div className="dash-empty">
            <div className="dash-empty-icon">📚</div>
            <div className="dash-empty-title">{t('tutorDetail.noBatches', 'No Batches Assigned')}</div>
            <p className="dash-empty-sub">{t('tutorDetail.manageInSchedule', 'Batches can be created and assigned in Class Schedule.')}</p>
          </div>
        ) : (
          batches.map(b => (
            <div key={b.id || Math.random()} className="dash-list-item">
              <div className="dash-avatar" style={{ background:'var(--off-white)', color:'var(--wine)', fontSize:20 }}>🕐</div>
              <div className="dash-list-item-main" onClick={() => b.id && navigate(`/batches/${b.id}`)} style={{ cursor:'pointer' }}>
                <div className="dash-list-item-name">{tField(b.name, i18n.language)}</div>
                <div style={{ display:'flex', gap:12, marginTop:4, flexWrap:'wrap', alignItems:'center' }}>
                  <span className={`badge ${LEVEL_BADGE[b.level] || 'badge-muted'}`}>{b.level}</span>
                  <span className="text-sm font-bold" style={{ color:'var(--wine)' }}>
                    {b.days?.join(', ')} | {b.startTime} - {b.endTime}
                  </span>
                  <span className="text-sm text-muted">👥 {b.students?.length || 0} / {b.maxStudents}</span>
                </div>
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <span className="dash-list-arrow" onClick={() => b.id && navigate(`/batches/${b.id}`)} style={{ cursor:'pointer' }}>›</span>
              </div>
            </div>
          ))
        )}
      </div>
    </Layout>
  );
}
